/* eslint-disable @typescript-eslint/member-ordering */
import { DoCheck, inject, OnDestroy, Type } from '@angular/core';
import { Subscription } from 'rxjs';
import {ApBaseClass, GenericControlValueAccessor, stringifyClass} from 'webcommon/shared';
import { AbstractControl, ControlValueAccessor, NgControl } from '@angular/forms';

// chain this CVA off another one attached to the same element
export function ApChainCVA<TBaseCVA extends ControlValueAccessor>(BaseCVA: Type<TBaseCVA>) {
  return <TValueInput, TValueOutput = TValueInput>() => {
    return class extends ApBaseClass implements GenericControlValueAccessor<TValueInput, TValueOutput>, DoCheck, OnDestroy {
      // This is the AbstractControl attached to this Directive/Component
      // via [formControl] [formControlName] [ngModel]
      // This value can change over time, but it is expected to have an initial value during ngOnInit if NgControl is injected.
      // This is why the FormControlDirective.ngOnChanges() reacts to the potential change of the FormControl.
      get _outerControl() {
        return this.ngControlDirective.control;
      }

      __previousOuterControl?: AbstractControl | null;
      __controlTeardown?: Subscription;

      readonly ngControlDirective: NgControl = inject(NgControl, {
        self: true,
      });

      readonly baseCVA: TBaseCVA;

      constructor(
      ) {
        super();
        const baseCVA = this.ngControlDirective.valueAccessor;
        if (!baseCVA) {
          throw new Error('No baseCVA.');
        }
        if (!(baseCVA instanceof BaseCVA)) {
          throw new Error(`Invalid baseCVA. expected type ${stringifyClass(BaseCVA)}, but was ${stringifyClass(baseCVA)}`);
        }
        this.baseCVA = baseCVA;
        this.ngControlDirective.valueAccessor = this;
      }

      ngDoCheck() {
        // track the outerControl, in case any setup needs to happen for a new one
        const outerControl = this._outerControl;
        if (outerControl !== this.__previousOuterControl) {
          this.__previousOuterControl = outerControl;
          if (outerControl) {
            this._onOuterControlChanged(outerControl);
          }
        }
      }

      registerOnChange(fn: (value: TValueOutput) => void): void {
        this.baseCVA.registerOnChange(fn);
      }

      registerOnTouched(fn: () => void): void {
        this.baseCVA.registerOnTouched(fn);
      }

      writeValue(obj: TValueInput): void {
        this.baseCVA.writeValue(obj);
      }

      setDisabledState(isDisabled: boolean): void {
        this.baseCVA.setDisabledState?.(isDisabled);
      }

      // called when outerControl changes
      _onOuterControlChanged(_outerControl: AbstractControl): void {
        this.__controlTeardown?.unsubscribe();
        this.__controlTeardown = this._setupOuterControl(_outerControl);
      }

      // this is just a convenience method so that the child class doesn't have to do the boilerplate
      // to manage subscriptions if the AbstractControl changes
      _setupOuterControl(_outerControl: AbstractControl): Subscription | undefined {
        return undefined;
      }

      override ngOnDestroy(): void {
        this.__controlTeardown?.unsubscribe();
        this.__controlTeardown = undefined;
        super.ngOnDestroy();
      }
    };
  };
}
